// @flow
import React, {Component} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Text} from 'trpc-react-native-ui';
import theme, {getCatalogStyle, getUsageStyle} from '../../theme-config';
import I18n from '../../lib/i18n';
import {formatValue} from '../../lib/usage-formatters';
import OptionRow from './OptionRow';
import Icon from './Icon';

const CIRCLE_DIAMETER = 70;
const CONTAINER_PADDING_HORIZONTAL = 10;
const DEFAULT_ITEM_MARGIN_HORIZONTAL = 15;
const PEEK_VISIBLE_ITEMS = 3.5; // 3 full + half of the 4th visible at first render

export default class PlanSummary extends Component<Props, State> {
  styles = getStyles();
  state = {scrollViewWidth: null};

  _onScrollViewLayout = (event: Object) => {
    const width = event.nativeEvent.layout.width;
    if (width && width !== this.state.scrollViewWidth) {
      this.setState({scrollViewWidth: width});
    }
  };

  _getOverflowItemMarginHorizontal = (): number => {
    const {scrollViewWidth} = this.state;
    if (!scrollViewWidth) return DEFAULT_ITEM_MARGIN_HORIZONTAL;
    const availableWidth = scrollViewWidth - CONTAINER_PADDING_HORIZONTAL * 2;
    const slotWidth = availableWidth / PEEK_VISIBLE_ITEMS;
    return Math.max(
      DEFAULT_ITEM_MARGIN_HORIZONTAL,
      (slotWidth - CIRCLE_DIAMETER) / 2,
    );
  };

  renderCreditableOption = (option: Object, marginHorizontal: number) => {
    const {inversedColors = false} = this.props;
    const type = option.type;
    const style = getCatalogStyle(type, theme);
    const emptyPlan = !option.characteristic;

    let formattedValue;
    if (!emptyPlan) {
      const value = option.characteristic?.value;
      if (value === 0) {
        return null;
      }

      let unit = option.characteristic?.unit;
      if (type === 'SMS') {
        unit = 'SMS';
      }
      const unitWhenZero = unit === 'DURATION' ? 'min' : undefined;
      formattedValue = isNaN(value)
        ? I18n.t(`VALUE_${value}`)
        : formatValue(value, unit, undefined, undefined, unitWhenZero);
    }

    let iconBorderColor = theme.signUpIconBorderColor;
    if (inversedColors) {
      iconBorderColor = getUsageStyle(`${type}_CREDIT`, theme).borderColor;
    }

    if (emptyPlan) {
      iconBorderColor = theme.gray.normalLight;
    }

    return (
      <View key={type} style={[this.styles.iconContainer, {marginHorizontal}]}>
        <View
          style={[
            this.styles.creditableOptionCircleContainer,
            {borderColor: iconBorderColor},
          ]}>
          <Icon
            name={style.icon}
            color={
              emptyPlan
                ? theme.gray.normalLight
                : inversedColors
                ? getUsageStyle(`${type}_CREDIT`, theme).iconColor
                : theme.signUpIconColor
            }
            size={26}
          />
        </View>
        <Text
          style={[
            this.styles.creditableOptionValue,
            !!inversedColors && this.styles.creditableOptionValueInversedColors,
          ]}>
          {formattedValue}
        </Text>
      </View>
    );
  };

  getServiceOptionLabel = (option: Object) => {
    const {name} = option;
    const characteristic = !!option.characteristics
      ? option.characteristics[0]
      : option.characteristic;

    return {
      title: I18n.t(`catalog.${name}`, {
        value: characteristic
          ? formatValue(characteristic.value, characteristic.unit)
          : '',
      }),
      legend: I18n.has(`catalog.${name}_LARGE`)
        ? I18n.t(`catalog.${name}_LARGE`)
        : null,
    };
  };

  getSecondaryCharacteristicOptionLabel = (characteristic: Object) => {
    return {
      title: I18n.t(`catalog.${characteristic.name}_DETAIL`, {
        value: formatValue(characteristic.value, characteristic.unit),
      }),
    };
  };

  render() {
    const styles = this.styles;
    const {
      options,
      secondaryCharacteristicsOptions = [],
      doSubscriptionOptionsIncludeUnavailableOption,
      isActivation,
    } = this.props;

    if (doSubscriptionOptionsIncludeUnavailableOption && isActivation) {
      return (
        <View>
          <Text
            style={[
              this.styles.infoText,
              !this.props.fromDashboard && this.styles.infoTextWithMargin,
            ]}>
            {this.props?.unavailablePlanText}
          </Text>
        </View>
      );
    }

    return (
      <View testID="planSummary">
        {!!options.creditable.length && (() => {
          const itemMarginHorizontal =
            options.creditable.length > 4
              ? this._getOverflowItemMarginHorizontal()
              : DEFAULT_ITEM_MARGIN_HORIZONTAL;
          return (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              onLayout={this._onScrollViewLayout}
              contentContainerStyle={styles.creditableOptionsContainer}>
              {options.creditable.map(option =>
                this.renderCreditableOption(option, itemMarginHorizontal),
              )}
            </ScrollView>
          );
        })()}
        {(!!options.service.length ||
          !!secondaryCharacteristicsOptions.length) && (
          <View style={styles.serviceOptionsContainer}>
            {options.service
              .map(this.getServiceOptionLabel)
              .map(({title, legend}) => (
                <OptionRow key={title} title={title} legend={legend} />
              ))}
            {secondaryCharacteristicsOptions
              .map(this.getSecondaryCharacteristicOptionLabel)
              .map(({title}) => (
                <OptionRow key={title} title={title} />
              ))}
          </View>
        )}
      </View>
    );
  }
}

type State = {
  scrollViewWidth: ?number,
};

type Props = {
  options: {
    creditable: Object[],
    service: Object[],
  },
  inversedColors?: boolean,
  isActivation?: boolean,
  secondaryCharacteristicsOptions: Object[],
  doSubscriptionOptionsIncludeUnavailableOption: boolean,
  unavailablePlanText: string,
  fromDashboard: boolean,
};

const getStyles = () =>
  StyleSheet.create({
    creditableOptionsContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingHorizontal: 10,
      marginBottom: 20,
    },
    iconContainer: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    creditableOptionCircleContainer: {
      width: 70,
      height: 70,
      borderColor: theme.signUpIconInCircleBackgroundColor,
      borderWidth: 2,
      borderRadius: 35,
      alignItems: 'center',
      justifyContent: 'center',
    },
    creditableOptionValue: {
      marginTop: 6,
      color: theme.signUpTextColor,
      fontSize: 16,
      textAlign: 'center',
    },
    creditableOptionValueInversedColors: {
      color: theme.gray.normal,
    },
    serviceOptionsContainer: {
      marginHorizontal: 10,
    },
    icon: {
      fontSize: 18,
      marginRight: 20,
      color: theme.secondaryColor,
    },
    legend: {
      color: theme.gray.light,
    },
    infoText: {
      textAlign: 'center',
    },
    infoTextWithMargin: {
      marginLeft: 12,
    },
  });
